import { actionChannel, call, fork, put, select, take } from 'redux-saga/effects';
import { buffers } from 'redux-saga';

import { Messages } from '../../codegen/Localize';

import { requestFailed } from '../../app/actions/RequestFailedAction';
import { ServiceLocator } from '../../app/ServiceLocator';

import { MetricDataPoints } from '../resources/MetricDataPoints';
import { IMetricDataList } from '../../ts/ServerApi/types/IMetricDataList';

import { metricsDataRequestFailed, metricsDataRequestStart, metricsDataRequestSucceeded } from '../actions/MetricsDataActions';
import { UpdateMetricsDataAction, UpdateMetricsData } from '../actions/UpdateMetricsActions';
import { getMetricsDataState, notLoadedData } from '../selectors';

// The number of Metric data requests to pre-allocate for.
const STARTING_DATA_BUFFER_SIZE = 20;

// In App, you can dispatch an action whenever you want to display metric data
// There is one `AppState` level object responsible for metric data points, `MetricsDataState`.
// Blocking:
// - Receive request for Metric Data
// - Filter requested ids to only those that are in `NOT_LOADED` state
// - Send Action to update filtered ids to `LOADING` state
// Non-Blocking:
// - Make network request for filtered ids
// - Receive results of network request
// - Send action to update successful ids to `LOADED` with returned data
// - Send action to update unsuccessful ids to `FAILED` with returned error info
export function* metricsDataSaga() {
  const requestChannel = yield actionChannel(UpdateMetricsData, buffers.expanding(STARTING_DATA_BUFFER_SIZE));
  while (true) {
    const action = yield take(requestChannel);
    yield call(startMetricDataUpdate, action);
  }
}

export function* startMetricDataUpdate(action: UpdateMetricsDataAction) {
  try {
    if (action.metrics.length === 0) {
      return;
    }
    const ids = action.metrics.map(m => m.id);
    const state = yield select(getMetricsDataState);
    const notLoadedIds = notLoadedData(state, ids);
    if (notLoadedIds.length === 0) {
      return;
    }
    yield put(metricsDataRequestStart(notLoadedIds));

    yield fork(requestDataPointsInBatches, notLoadedIds);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

const METRICS_PAGE_SIZE = 20;

export function* requestDataPointsInBatches(ids: ReadonlyArray<string>) {
  try {
    for (let i = 0; i < ids.length; i += METRICS_PAGE_SIZE) {
      const response = yield call(requestMetricData, ids.slice(i, i + METRICS_PAGE_SIZE));
      if (response.data && !isEmpty(response.data)) {
        yield put(metricsDataRequestSucceeded(response.data));
      }
      if (response.failures && !isEmpty(response.failures)) {
        yield put(metricsDataRequestFailed(response.failures));
      }
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

const isEmpty = (obj: any) => !Object.keys(obj).length;

export async function requestMetricData(ids: string[]): Promise<IMetricDataList> {
  const metricsResource = ServiceLocator.get<MetricDataPoints>('MetricDataPoints');
  return await metricsResource.getMetricDataPoints(ids);
}
